import { PayloadAction } from '@reduxjs/toolkit';
import {
  IContractDoc,
  ContractDocSource,
  ContractStatus,
  isAmieWebContractHtmlEditorByStatus,
  isPlacementContractPdfByStatus,
  isValidPlacementContractDocumentImageGuid,
  opensInCandidateContractHtmlEditor,
  resolvePlacementContractDocumentTypeId,
} from './types';
import { trackException } from 'app-insights/appInsightsTracking';
import { ExceptionType } from 'app/enums/Common';
import { all, call, put, select } from 'redux-saga/effects';
import { pContractDocEditorActions } from './slice';
import { getDocumentFromUrlId, requestMergeTokenList, requestPdfDocument, requestTemplates } from './saga';
import queryString from 'query-string';
import { selectDocumentView } from './selector';
import { base64DocumentToURL } from 'app/helpers/fileHelper';
import { globalActions } from 'app/ApplicationRoot/Global.redux';
import i18next from 'i18next';

export function* onInit(action: PayloadAction<{ placementId: number }>) {
  try {
    const [templates] = yield all([call(requestTemplates, action), call(requestMergeTokenList)]);
    const document = yield call(getDocumentFromUrlId, { templates });

    const { default_selection: defaultSelection, documentId: docIdfromUrl } = queryString.parse(window.location.search);

    const draftDoc = templates.find(
      x =>
        x.status === ContractStatus.draft ||
        (x.source === ContractDocSource.amieWeb && x.status === ContractStatus.pending),
    );
    const contractDocFromUrl = !!document
      ? templates.find(
          x =>
            String(x.documentId) === String(document.documentId) ||
            String(x.id) === String(document.documentId),
        )
      : undefined;
    const initialSelected =
      !!docIdfromUrl ? contractDocFromUrl ?? null : defaultSelection === 'draft' && !!draftDoc ? draftDoc : null;
    yield put(pContractDocEditorActions.setSelectedTemplate(initialSelected));
    if (isAmieWebContractHtmlEditorByStatus(initialSelected)) {
      yield put(pContractDocEditorActions.getContractCatalogTemplates());
    } else {
      yield put(pContractDocEditorActions.setCatalogTemplates([]));
    }
    if (contractDocFromUrl) yield put(pContractDocEditorActions.setPdfView(document));
    else if (!!docIdfromUrl && !contractDocFromUrl) {
      const urlParams = new URLSearchParams(window.location.search);
      urlParams.delete('documentId');
      urlParams.delete('documentImageId');
      urlParams.delete('source');
      window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
    }
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'onInit',
        area: 'src/store/reducx-store/placement-client-confirmation/business.saga',
      },
    });
  }
}

export function* onContractDocClick(action: PayloadAction<{ contract: IContractDoc | null }>) {
  try {
    const { contract } = action.payload;
    const { renderPdfView, renderPreviewScreen } = yield select(selectDocumentView);

    const urlParams = new URLSearchParams(window.location.search);

    if (urlParams.has('default_selection')) {
      urlParams.delete('default_selection');
    }

    if (!contract) {
      yield put(pContractDocEditorActions.setSelectedTemplate(null));
      yield put(pContractDocEditorActions.setCatalogTemplates([]));
      if (renderPdfView || renderPreviewScreen) yield put(pContractDocEditorActions.resetToDocView());
      urlParams.delete('documentImageId');
    } else if (opensInCandidateContractHtmlEditor(contract)) {
      yield put(pContractDocEditorActions.resetToDocView());
      urlParams.delete('documentId');
      urlParams.delete('documentImageId');
      urlParams.delete('source');
      yield put(pContractDocEditorActions.setSelectedTemplate(contract));
      if (isAmieWebContractHtmlEditorByStatus(contract)) {
        yield put(pContractDocEditorActions.getContractCatalogTemplates());
      } else {
        yield put(pContractDocEditorActions.setCatalogTemplates([]));
      }
    } else {
      const useContractDocumentTypeApi =
        contract.source === ContractDocSource.amieClassic || isPlacementContractPdfByStatus(contract);

      const hasLegacyDownloadGuid =
        !useContractDocumentTypeApi &&
        contract.source === ContractDocSource.amieWeb &&
        isValidPlacementContractDocumentImageGuid(contract.documentImageId);

      const callStatus = yield call(requestPdfDocument, {
        payload: {
          documentId: String(resolvePlacementContractDocumentTypeId(contract) ?? contract.documentId),
          source: contract.source,
          ...(useContractDocumentTypeApi
            ? { useContractDocumentTypeApi: true as const }
            : hasLegacyDownloadGuid
              ? { documentImageId: String(contract.documentImageId).trim() }
              : {}),
        },
        type: pContractDocEditorActions.getPdfDocument.type,
      } as Parameters<typeof requestPdfDocument>[0]);
      if (callStatus) {
        urlParams.set('documentId', String(resolvePlacementContractDocumentTypeId(contract) ?? contract.documentId));
        if (useContractDocumentTypeApi || contract.source === ContractDocSource.amieClassic) {
          urlParams.delete('documentImageId');
        } else if (hasLegacyDownloadGuid) {
          urlParams.set('documentImageId', String(contract.documentImageId).trim());
        } else urlParams.delete('documentImageId');
        urlParams.set('source', contract.source);
        yield put(pContractDocEditorActions.setSelectedTemplate(contract));
        yield put(pContractDocEditorActions.setCatalogTemplates([]));
      }
    }
    window.history.pushState(null, null, `${window.location.pathname}?${urlParams.toString()}`);
  } catch (error) {
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'onContractDocClick',
        area: 'src/store/reducx-store/placement-client-confirmation/business.saga',
      },
    });
  }
}

export function* downloadSelectedCandidateContract() {
  try {
    const { document } = yield select(selectDocumentView);
    if (document?.documentId) {
      const { dispose } = base64DocumentToURL(document.data, {
        fileType: 'application/pdf',
        fileName: document.fileName,
        download: true,
      });
      yield put(
        globalActions.setSnackBar({
          message: i18next.t('templateEditor.placement.snackbar.contractEditor.downloadSuccess'),
          severity: 'success',
        }),
      );
      dispose();
    }
  } catch (error) {
    yield put(
      globalActions.setSnackBar({
        message: i18next.t('templateEditor.placement.snackbar.contractEditor.downloadFailure'),
        severity: 'error',
      }),
    );
    trackException({
      exception: error,
      properties: {
        name: ExceptionType.CommonSAGAError,
        functionName: 'downloadSelectedCandidateContract',
        area: 'src/store/reducx-store/placement-client-confirmation/business.saga',
      },
    });
  }
}
